import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";

import {AlertProvider, useAlert} from "../Components/AlertContext";
import {ConfirmationProvider} from "../Components/ConfirmationContext";

import Breadcrumbs from "../Components/Breadcrumbs";
import Button from "../Components/Buttons";
import { CustomInput, CustomTextarea, CustomToggle} from "../Components/CustomFormElements";
import {isEmpty} from "../utils/validationUtil";
import PageLoader from "../Components/PageLoader";

import "../Components/TaxonomyDictionary.css";
import {FragmentApiProvider, useFragmentApi} from "../Services/FragmentApiProvider";

function FragmentDetail({canEdit}) {
  const {organizationId, fragmentId} = useParams();
  const navigate = useNavigate();
  const {showAlert} = useAlert();

  const {
    getFragmentById,
    createFragment,
    updateFragment,
  } = useFragmentApi();


  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [name, setName] = useState("");
  const [template, setTemplate] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const fetchFragment = async () => {
      setLoading(true);
      try {
        if (fragmentId !== "new-fragment") {
          const response = await getFragmentById(fragmentId);
          setName(response.name);
          setTemplate(response.template || "");
          setIsActive(response.is_active);
          setIsNew(false);
          setIsDisabled(!canEdit);

        } else {
          setIsNew(true);
          setIsDisabled(!canEdit);
        }
      } catch {
        showAlert("fail", "Failed to load fragment");
      } finally {
        setLoading(false);
      }
    };

    fetchFragment();
  }, [fragmentId, canEdit]);

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/fragments`);
  };

  const onSave = async () => {
    if (isEmpty(name)) {
      showAlert("fail", "Please enter a name");
      return;
    }

    const data = {name: name, template: template, is_active: isActive};

    setLoading(true);
    try {
      if (isNew) {
        await createFragment(data);
        showAlert("success", "Fragment created!");
        navigate(`/organizations/${organizationId}/fragments`);
      } else {
        await updateFragment(fragmentId, data);
        showAlert("success", "Fragment updated!");
      }
    } catch {
      showAlert("fail", "Failed to save Fragment");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PageLoader/>;

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">
            {isNew ? "Create Fragment" : `Edit Fragment: ${name}`}
          </h2>

          <div className='input-block d-flex flex-row justify-between'>
            <div className='input-description'>
              <div className='input-title'>Fragment name</div>
              <div className='small-font'>Enter fragment name</div>
            </div>
            <div className='align-wrapper d-flex align-center'>
              <CustomInput
                  id="fragment-name"
                  label="Fragment Name"
                  placeholder="Enter name"
                  type="text"
                  value={name}
                  onChange={(id, value) => setName(value)}
                  disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Fragment template</div>
              <div className="small-font">Enter template</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                  id="template"
                  label="Template"
                  name="template"
                  type="text"
                  addedText={template}
                  onChange={(value) => setTemplate(value)}
                  resizable={false}
                  disabled={isDisabled}
                  height="200px"
              />
            </div>
          </div>
          <div className='input-row input-block d-flex flex-row justify-between'>
            <div className='input-description'>
              <div className='input-title'>Activate fragment</div>
              <div className='small-font'>Set fragment to be active or inactive</div>
            </div>
            <div className='align-wrapper d-flex align-center'>
              <CustomToggle
                  id="toggle-status"
                  label={isActive ? 'Active' : 'Inactive'}
                  checked={isActive}
                  onChange={(id, value) => setIsActive(value)}
                  disabled={false}
              />
            </div>
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FragmentDetailWrapper({canEdit}) {
  return (
      <AlertProvider>
        <ConfirmationProvider>
          <FragmentApiProvider>
            <FragmentDetail canEdit={canEdit}/>
          </FragmentApiProvider>
        </ConfirmationProvider>
      </AlertProvider>
  );
}

export default FragmentDetailWrapper;
